import { addToast, Button } from '@octano/global-ui';
import { Col, Row } from 'reactstrap';
import StudentTabsHeader from '../StudentTabsHeader';
import { isNumber } from '../../../../utils/math';
import { useParams } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { StudentDocumentsLoader } from './parts/StudentDocumentsLoader';
import { DocumentsTuitionTable } from './parts/DocumentsTuitionTable';
import { SectionTitle } from '../../../../components/text';
import { useTexts } from './hooks/useTexts';
import { DocumentsOthersTable } from './parts/DocumentsOthersTable';
import { StudentDocumentCreateModal } from './parts/StudentDocumentCreateModal';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import { StudentDocument } from './types';
import { deleteStudentDocument } from '../../api/studentDocumentsRequests';
import useUserState from '../../../../hooks/useUserState';
import { PermissionName } from '../../../../types/Auth';

export function StudentDocuments() {
  const texts = useTexts();
  const { isAuthorizedTo } = useUserState();

  const [createOpen, setCreateOpen] = useState(false);
  const [
    deleteConfirmDocument,
    setDeleteConfirmDocument,
  ] = useState<StudentDocument>();

  const { studentId } = useParams<{ studentId: string }>();
  const studentIdInt = useMemo(() => {
    if (isNumber(studentId)) {
      return Number(studentId);
    }
    return null;
  }, [studentId]);
  if (!studentIdInt) {
    throw new Error('Invalid studentId');
  }

  const handleDeleteStudentdocument = useCallback(
    async (studentDocumentId: number, refresh: () => void) => {
      const result = await deleteStudentDocument({
        studentId: studentIdInt,
        studentDocumentId,
      });
      if (result.error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: texts.modalDelete.error,
        });
      } else {
        addToast({
          icon: 'check',
          color: 'success',
          text: texts.modalDelete.success,
        });
        refresh();
      }
    },
    [studentIdInt, texts],
  );

  const hasPermission = useMemo(
    () =>
      isAuthorizedTo(
        [PermissionName.STUDENT_MANAGEMENT_STUDENT_FILE_MANAGE],
        true,
      ),
    [isAuthorizedTo],
  );

  return (
    <StudentDocumentsLoader studentId={studentIdInt}>
      {({ data, refresh }) => (
        <>
          <StudentTabsHeader
            id={data.student.rut}
            name={data.student.fullName}
          />
          <div className="my-4">
            <SectionTitle text={texts.documentsTuitionTitle} />
          </div>
          <div className="my-4">
            <DocumentsTuitionTable
              studentId={studentIdInt}
              data={data.documentsTuition}
            />
          </div>
          <Row className="my-4">
            <Col>
              <SectionTitle text={texts.documentsOthersTitle} />
            </Col>
            {hasPermission && (
              <Col xs="auto">
                <Button
                  text={texts.createStudentDocument}
                  color="primary"
                  icon="plus"
                  size="md"
                  outlined
                  onClick={() => {
                    setCreateOpen(true);
                  }}
                />
              </Col>
            )}
          </Row>
          <div className="my-4">
            <DocumentsOthersTable
              studentId={studentIdInt}
              data={data.documentsOther}
              hasPermissionToDelete={hasPermission}
              onDelete={(document: StudentDocument) => {
                setDeleteConfirmDocument(document);
              }}
            />
          </div>
          {createOpen && (
            <StudentDocumentCreateModal
              studentId={studentIdInt}
              closeModal={(result) => {
                setCreateOpen(false);
                if (result) {
                  refresh();
                }
              }}
            />
          )}

          <ConfirmationModal
            iconName="warning"
            isOpen={Boolean(deleteConfirmDocument)}
            toggle={() => setDeleteConfirmDocument(undefined)}
            title={texts.modalDelete.title}
            body={texts.modalDelete.body}
            primaryBtn={{
              text: texts.modalDelete.delete,
              action: () => {
                if (deleteConfirmDocument) {
                  handleDeleteStudentdocument(
                    deleteConfirmDocument.id,
                    refresh,
                  );
                }
                setDeleteConfirmDocument(undefined);
              },
            }}
            secondaryBtn={{
              text: texts.modalDelete.cancel,
              action: () => {
                setDeleteConfirmDocument(undefined);
              },
            }}
          />
        </>
      )}
    </StudentDocumentsLoader>
  );
}
